// Persistent MAVIS dock — mounts once inside AdminShell so the assistant
// survives navigation between admin pages. Reads live page context and
// conversation state from providers.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Send,
  Loader2,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  X,
  AlertTriangle,
  ClipboardCheck,
  MessageSquare,
  RefreshCw,
} from "lucide-react";
import { ProposedChangeCard, type Proposal } from "@/components/assistant/ProposedChangeCard";
import { ProposalGroupCard } from "@/components/assistant/ProposalGroupCard";
import { groupProposals } from "@/lib/proposalGroups";
import { CurrentWorkflowPanel } from "@/components/assistant/CurrentWorkflowPanel";
import { useAssistantConversation, type AssistantMessage } from "./AssistantConversation";
import { usePageContext } from "./PageContext";
import { proactiveInsightsForPage, suggestionsForPage } from "./pageInsights";

const COLLAPSE_KEY = "eos:collapsed";

export function EosDock({ floating = false }: { floating?: boolean } = {}) {
  const {
    messages,
    workflow,
    loading,
    error,
    functionName,
    send,
    clear,
    retryLast,
    setWorkflow,
    pageContext,
  } = useAssistantConversation();
  const { clear: clearPageOverride } = usePageContext();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (floating) return true;
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  });
  useEffect(() => {
    if (floating) return;
    if (typeof window === "undefined") return;
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed, floating]);

  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [activeProposalIdx, setActiveProposalIdx] = useState(0);
  const prevProposalCountRef = useRef(0);
  const prevMessageCountRef = useRef(messages.length);
  const [unread, setUnread] = useState(0);

  // Collapsed → count new assistant messages as unread
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const delta = messages.length - prevMessageCountRef.current;
      if (collapsed) {
        // Only count assistant additions
        const added = messages.slice(-delta).filter((m) => m.role === "assistant").length;
        if (added > 0) setUnread((n) => n + added);
      }
      prevMessageCountRef.current = messages.length;
    }
  }, [messages, collapsed]);

  useEffect(() => {
    if (!collapsed) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [collapsed]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const pendingProposals = useMemo<Proposal[]>(() => {
    const seen = new Set<string>();
    const out: Proposal[] = [];
    for (const m of messages) {
      for (const p of m.proposals ?? []) {
        if (p?.pending_id && !seen.has(p.pending_id)) {
          seen.add(p.pending_id);
          out.push(p);
        }
      }
    }
    return out;
  }, [messages]);

  useEffect(() => {
    if (pendingProposals.length > prevProposalCountRef.current) {
      setActiveProposalIdx(pendingProposals.length - 1);
      const newest = pendingProposals[pendingProposals.length - 1];
      setTimeout(() => scrollToProposal(newest.pending_id), 60);
    }
    prevProposalCountRef.current = pendingProposals.length;
  }, [pendingProposals]);

  const scrollToProposal = (pendingId: string) => {
    const root = panelRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-proposal-id="${CSS.escape(pendingId)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 1500);
    }
  };

  const suggestions = useMemo(() => suggestionsForPage(pageContext), [pageContext]);
  const insights = useMemo(() => proactiveInsightsForPage(pageContext), [pageContext]);

  const submit = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    await send(q);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // ------ Collapsed rail ------
  if (collapsed) {
    if (floating) {
      return (
        <button
          onClick={() => setCollapsed(false)}
          className="fixed right-6 bottom-6 z-40 h-12 px-4 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center gap-2 hover:scale-105 transition-transform"
          aria-label="Ask MAVIS"
        >
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-medium">Ask MAVIS</span>
          {(unread > 0 || pendingProposals.length > 0) && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {pendingProposals.length > 0 ? pendingProposals.length : unread}
            </span>
          )}
        </button>
      );
    }
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed right-4 bottom-4 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Open MAVIS assistant"
      >
        <Sparkles className="h-6 w-6" />
        {(unread > 0 || pendingProposals.length > 0) && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
            {pendingProposals.length > 0 ? pendingProposals.length : unread}
          </span>
        )}
      </button>
    );
  }

  // ------ Expanded dock ------
  return (
    <aside
      ref={panelRef}
      className="hidden lg:flex fixed right-0 top-14 bottom-0 z-30 w-[360px] xl:w-[400px] flex-col border-l border-border bg-card/95 backdrop-blur"
    >
      {/* Header */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b bg-gradient-to-r from-primary/5 to-transparent">
        <div className="h-7 w-7 rounded-full bg-primary/15 flex items-center justify-center">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm leading-tight">MAVIS</div>
          <div className="text-[11px] text-muted-foreground truncate">Executive Operating System</div>
        </div>
        {messages.length > 0 && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={clear} title="Clear conversation">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setCollapsed(true)}
          title="Collapse MAVIS"
          aria-label="Collapse MAVIS"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Context breadcrumb */}
      <div className="shrink-0 px-3 py-2 border-b bg-muted/30 text-xs flex items-center gap-1.5 flex-wrap">
        <span className="text-muted-foreground uppercase tracking-wide text-[10px]">Context:</span>
        <span className="font-medium text-foreground">{pageContext.sectionLabel}</span>
        {pageContext.selection && (
          <>
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium text-foreground truncate max-w-[180px]">
              {pageContext.selection.label}
            </span>
            <button
              onClick={clearPageOverride}
              className="text-muted-foreground hover:text-foreground"
              title="Clear selection"
              aria-label="Clear selection"
            >
              <X className="h-3 w-3" />
            </button>
          </>
        )}
        {pageContext.filters && Object.keys(pageContext.filters).length > 0 && (
          <span className="text-muted-foreground truncate">
            · {filtersToString(pageContext.filters)}
          </span>
        )}
      </div>

      {/* Pending proposals banner */}
      {pendingProposals.length > 0 && (
        <div className="shrink-0 border-b border-primary/30 bg-primary/10 px-3 py-1.5 flex items-center gap-2 text-xs">
          <ClipboardCheck className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="font-medium">
            {pendingProposals.length} pending proposal{pendingProposals.length === 1 ? "" : "s"}
          </span>
          <div className="ml-auto flex items-center gap-1">
            {pendingProposals.length > 1 && (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => {
                    const next = Math.max(0, activeProposalIdx - 1);
                    setActiveProposalIdx(next);
                    scrollToProposal(pendingProposals[next].pending_id);
                  }}
                  disabled={activeProposalIdx === 0}
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <span className="tabular-nums text-muted-foreground">
                  {activeProposalIdx + 1}/{pendingProposals.length}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => {
                    const next = Math.min(pendingProposals.length - 1, activeProposalIdx + 1);
                    setActiveProposalIdx(next);
                    scrollToProposal(pendingProposals[next].pending_id);
                  }}
                  disabled={activeProposalIdx >= pendingProposals.length - 1}
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </>
            )}
            <Button
              size="sm"
              className="h-6 text-[11px] px-2"
              onClick={() => scrollToProposal(pendingProposals[activeProposalIdx].pending_id)}
            >
              Review
            </Button>
          </div>
        </div>
      )}

      {/* Scroll area — the ONE flexible region. Workflow lives inside so a
          tall proposal/workflow never pushes the composer off-screen. */}
      <div
        ref={scrollerRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-3 space-y-3"
      >
        {workflow && (
          <CurrentWorkflowPanel
            workflow={workflow}
            functionName={functionName}
            onChange={setWorkflow}
            onSendMessage={(text) => send(text)}
          />
        )}

        {messages.length === 0 && (
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                On this page
              </p>
              <div className="flex flex-wrap gap-1.5">
                {insights.map((chip) => (
                  <button
                    key={chip.label}
                    onClick={() => send(chip.prompt)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                      chip.tone === "warning"
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 hover:bg-amber-500/20"
                        : "border-border hover:border-primary/40 hover:bg-primary/5"
                    }`}
                  >
                    {chip.tone === "warning" && (
                      <AlertTriangle className="h-2.5 w-2.5 inline-block mr-1 -mt-0.5" />
                    )}
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Try asking
              </p>
              <div className="flex flex-col gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-left text-xs px-2.5 py-1.5 rounded-md border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t">
              <MessageSquare className="h-3 w-3" />
              Conversation stays open while you navigate between admin pages.
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <MessageBubble key={i} message={m} functionName={functionName} onRetry={retryLast} />
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Thinking…
          </div>
        )}
        {error && (
          <div className="text-xs text-destructive bg-destructive/10 rounded-md p-2">{error}</div>
        )}
      </div>

      {/* Composer — fixed at bottom, always visible. */}
      <form
        className="shrink-0 border-t p-2 flex items-center gap-1.5 bg-card"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholderFor(pageContext)}
          disabled={loading}
          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm h-9"
        />
        <Button type="submit" size="icon" className="h-8 w-8" disabled={loading || !input.trim()}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </Button>
      </form>
    </aside>
  );
}

function filtersToString(f: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(f)) {
    if (v === null || v === undefined || v === "" || v === "all") continue;
    parts.push(`${k}: ${String(v)}`);
  }
  return parts.join(" · ");
}

function placeholderFor(ctx: { sectionLabel: string; selection?: { label: string } | null }): string {
  if (ctx.selection) return `Ask about ${ctx.selection.label}…`;
  return `Ask about ${ctx.sectionLabel.toLowerCase()}…`;
}

function MessageBubble({
  message,
  functionName,
  onRetry,
}: {
  message: AssistantMessage;
  functionName: string;
  onRetry: () => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap">
          {message.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <div className="h-6 w-6 rounded-full bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
        <Sparkles className="h-3 w-3 text-primary" />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        {message.content && (
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</div>
        )}
        {(() => {
          const raw = (message.proposals ?? []).filter((p): p is Proposal => !!p?.pending_id);
          if (raw.length === 0) return message.proposalMissing ? <MalformedProposalWarning onRetry={onRetry} /> : null;
          const { groups, standalone } = groupProposals(raw);
          return (
            <>
              {groups.map((g) => (
                <ProposalGroupCard key={g.key} group={g} functionName={functionName} />
              ))}
              {standalone.map((p) => (
                <ProposedChangeCard key={p.pending_id} proposal={p} functionName={functionName} />
              ))}
              {message.proposals?.some((p) => !p?.pending_id) && (
                <MalformedProposalWarning onRetry={onRetry} />
              )}
            </>
          );
        })()}
        {message.actions && message.actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {message.actions.map((a, i) =>
              a.href ? (
                <Button key={i} asChild size="sm" variant="outline" className="h-7 text-xs">
                  <Link to={a.href}>
                    {a.label}
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </Button>
              ) : (
                <Badge key={i} variant="secondary" className="text-[10px]">
                  {a.label}
                </Badge>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MalformedProposalWarning({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1">
        A proposal was created but could not be displayed.{" "}
        <button className="underline font-medium" onClick={onRetry}>
          Reopen it.
        </button>
      </div>
    </div>
  );
}
